import { useEffect } from "react";
import { createPortal } from "react-dom";
import CloseModal from "../../Atomos/Icons/EditMode/CloseModal";

type ModalProps = {
  isModalOpen: boolean;
  toggleModal: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  titleColor?: string;
  compact?: boolean;
  /** When set with `compact`, inner header/body skip horizontal padding so the card can use outer `className` (e.g. `p-10`) only. */
  outerPaddingOnly?: boolean;
  /** When `compact`, default body uses a scroll + max height; set false for short static dialogs (avoids flex/clipping with outer padding). */
  compactBodyScrolls?: boolean;
};

const Modal: React.FC<ModalProps> = ({
  isModalOpen,
  toggleModal,
  title,
  children,
  className,
  bodyClassName,
  titleColor,
  compact = false,
  outerPaddingOnly = false,
  compactBodyScrolls = true,
}: ModalProps) => {
  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isModalOpen]);

  if (!isModalOpen) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center p-4"
      onClick={toggleModal}
    >

      <div
        className={`flex min-h-0 w-full max-w-[90vw] max-h-[min(90dvh,90vh)] flex-col overflow-hidden rounded-lg bg-white shadow-xl ${className ?? ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`flex shrink-0 items-start justify-between gap-6 bg-white ${
            compact
              ? outerPaddingOnly
                ? "pt-0 px-0"
                : "px-4 pt-4 pr-2"
              : ""
          }`}
        >
          <div
            className={`font-medium ${titleColor ?? "text-gray-font"} ${compact ? "text-lg pl-0 pr-2 pt-0" : "text-2xl px-6 md:px-10 pt-6 md:pt-10"}`}
          >
            {title}
          </div>
          <button
            type="button"
            className={`w-6 h-6 flex shrink-0 cursor-pointer fill-gray-font relative ${compact ? "mt-0 top-0 right-0" : "mt-1 top-2 right-2"}`}
            onClick={toggleModal}
            aria-label="Cerrar modal"
          >
            <CloseModal />
          </button>
        </div>

        <div
          className={`${
            compact
              ? compactBodyScrolls
                ? `min-h-0 max-h-[min(80vh,90vh)] overflow-y-auto pt-2 ${
                    outerPaddingOnly ? "px-0 pb-0" : "px-4 pb-4"
                  }`
                : `shrink-0 max-h-none !overflow-y-visible ${
                    outerPaddingOnly
                      ? "px-0 pb-0"
                      : "px-4 pb-4"
                  } pt-2`
              : "flex-1 min-h-[500px] overflow-y-auto px-6 md:px-10 pb-6 md:pb-10 pt-6"
          } ${bodyClassName ?? ""}`}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
